import { Button, Spinner, Table, ToggleButton } from "react-bootstrap"
import { AmountComponent } from "../AmountComponent"
import { useCallback, useEffect, useState } from "react"
import styles from "./Savings.module.css"
import classNames from "classnames"
import { AdjustmentMode, AssetId, GoalAssetCoordinates, GoalId, SavingsSummary } from "./model"
import { TransferModal } from "./TransferModal"
import { AdjustmentModal } from "./AdjustmentModal"
import _ from "lodash"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowDown, faArrowLeft, faArrowRight, faArrowUp, faEdit, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons"
import { EditionModal, EditionModalState } from "./EditionModal"
import { useAxios } from "../AxiosContext"
import { toast } from "react-toastify"
import { AxiosError } from "axios"
import { CurrencyAmount } from "../model/CurrencyAmount"


interface ClickState {
  source: GoalAssetCoordinates | null
  target: GoalAssetCoordinates | null
}

interface AdjustmentTarget {
  assetId: AssetId
  goalId?: GoalId
}

export const Savings: React.FC = () => {
  const axios = useAxios()

  const [loading, setLoading] = useState(false)
  const [summary, setSummary] = useState<SavingsSummary>({ goals: {}, assets: {}, mapping: {} })

  const orderOf = (assetId: AssetId) => {
    const asset = summary.assets[assetId]
    if ("managedSavingsAccount" in asset) {
      return asset.managedSavingsAccount.order
    } else {
      return asset.custom.order
    }
  }

  const sortedAssets = Object.entries(summary.assets).sort((a, b) => orderOf(a[0]) - orderOf(b[0]))
  const sortedGoals = Object.entries(summary.goals).sort((a, b) => a[1].order - b[1].order)

  const goalTotals = sortedGoals ? Object.fromEntries(Object.entries(summary.goals).map(([goalId,]) => {
    const mapping = summary.mapping[goalId]

    if (mapping) {
      const total = Object
        .values(mapping)
        .reduce((a: CurrencyAmount, b: CurrencyAmount) => {
          return { amount: a.amount + b.amount, currency: a.currency }
        }, { amount: 0, currency: "PLN" })
      return [goalId, total]
    } else {
      return [goalId, { amount: 0, currency: "PLN" }]
    }

  })) : {}

  const [clickState, setClickState] = useState<ClickState>({
    source: null,
    target: null
  })

  const [refreshCounter, setRefreshCounter] = useState(0)

  const [edition, setEdition] = useState(false)

  const [editModalState, setEditModalState] = useState<EditionModalState | null>(null)

  const onCellClicked = (assetId: AssetId, goalId: GoalId) => {
    if (!edition) {
      setClickState(state => {
        if (!state.source || state.target) {
          return {
            source: {
              goalId,
              assetId
            },
            target: null
          }
        } else {
          return {
            ...state,
            target: {
              goalId,
              assetId
            }
          }
        }
      })
    }
  }

  const cancelCellClicked = useCallback(() => {
    setClickState({
      source: null,
      target: null
    })
  }, [setClickState])

  const [adjustmentTarget, setAdjustmentTarget] = useState<AdjustmentTarget | null>(null)

  const transfer = async (amount: number) => {
    if (clickState.source && clickState.target) {
      try {
        const response = await axios.post(`/v1/savings/operations/transfer`, {
          amount: {
            amount,
            currency: "PLN"
          },
          source: {
            goalId: clickState.source.goalId,
            assetId: clickState.source.assetId
          },
          destination: {
            goalId: clickState.target.goalId,
            assetId: clickState.target.assetId
          }
        })
        setSummary(response.data)
        cancelCellClicked()
      } catch (e) {
        toast.error("Transfer failed — unknown error occurred")
        console.error(e)
      }
    }

  }

  const adjust = async (amount: number, mode: AdjustmentMode) => {
    if (adjustmentTarget) {
      console.log(`Adjusting ${amount} on asset`, adjustmentTarget)
      try {
        if (adjustmentTarget.goalId) {
          const response = await axios.put(`/v1/savings/operations/adjustment`, {
            amount: {
              amount,
              currency: "PLN"
            },
            target: {
              goalId: adjustmentTarget.goalId,
              assetId: adjustmentTarget.assetId
            },
            mode
          })
          setSummary(response.data)
        } else {
          await axios.put(`/v1/savings/assets/${adjustmentTarget.assetId}/balance`, {
            amount: {
              amount,
              currency: "PLN"
            },
            mode
          })
          setRefreshCounter(counter => counter + 1)
        }

        cancelCellClicked()
        setAdjustmentTarget(null)
      } catch (e) {
        toast.error("Adjustment failed — unknown error")
        console.error(e)
      }
    }
  }


  const onEscape = useCallback(() => {
    cancelCellClicked()
  }, [cancelCellClicked])

  const openAssetAdditionModal = () => {
    setEditModalState({
      mode: "add",
      type: "asset",
      currentName: "",
      onConfirm: async (name?: string) => {
        try {
          console.log(`Adding asset ${name}...`)
          await axios.post("/v1/savings/assets", {
            kind: "savings-account",
            currency: "PLN",
            name
          })
          setRefreshCounter(counter => counter + 1)
          setEditModalState(null)
        } catch (e) {
          toast.error("Asset addition failed — unknown error")
          console.error(e)
        }
      }
    })
  }

  const openAssetRenameModal = (assetId: AssetId) => {
    const asset = summary.assets[assetId]
    const unwrappedAsset = "managedSavingsAccount" in asset ? asset.managedSavingsAccount : asset.custom

    setEditModalState({
      mode: "rename",
      type: "asset",
      currentName: unwrappedAsset.name,
      onConfirm: async (name?: string) => {
        console.log(`Renaming asset ${assetId} to ${name}`)
        try {
          await axios.put(`/v1/savings/assets/${assetId}/name`, {
            name
          })
          setRefreshCounter(counter => counter + 1)
          setEditModalState(null)
        } catch (e) {
          toast.error("Asset renaming failed — unknown error")
          console.error(e)
        }
      }
    })
  }

  const openAssetRemovalModal = (assetId: AssetId) => {
    const asset = summary.assets[assetId]
    const unwrappedAsset = "managedSavingsAccount" in asset ? asset.managedSavingsAccount : asset.custom

    setEditModalState({
      mode: "delete",
      type: "asset",
      currentName: unwrappedAsset.name,
      onConfirm: async () => {
        console.log(`Removing asset ${assetId}`)
        try {
          await axios.delete(`/v1/savings/assets/${assetId}`)
          setRefreshCounter(counter => counter + 1)
          setEditModalState(null)
        } catch (error) {
          if (error instanceof AxiosError && error.response?.status === 400) {
            toast.error("Cannot remove the asset, it must be empty")
          } else {
            toast.error("Asset removal failed — unknown error")
          }
          console.error(error)
        }
      }
    })
  }

  const openGoalAdditionModal = () => {
    setEditModalState({
      mode: "add",
      type: "goal",
      currentName: "",
      onConfirm: async (name?: string) => {
        console.log(`Adding goal ${name}`)
        try {
          await axios.post("/v1/savings/goals", {
            name
          })
          setRefreshCounter(counter => counter + 1)
          setEditModalState(null)
        } catch (e) {
          toast.error("Goal addition failed — unknown error")
          console.error(e)
        }
      }
    })
  }

  const openGoalRenameModal = (goalId: GoalId) => {
    setEditModalState({
      mode: "rename",
      type: "goal",
      currentName: summary.goals[goalId].name,
      onConfirm: async (name?: string) => {
        console.log(`Renaming goal ${goalId} to ${name}`)
        try {
          await axios.put(`/v1/savings/goals/${goalId}/name`, {
            name
          })
          setRefreshCounter(counter => counter + 1)
          setEditModalState(null)
        } catch (e) {
          toast.error("Goal renaming failed — unknown error")
          console.error(e)
        }
      }
    })
  }

  const openGoalRemovalModal = (goalId: GoalId) => {
    setEditModalState({
      mode: "delete",
      type: "goal",
      currentName: summary.goals[goalId].name,
      onConfirm: async () => {
        console.log(`Removing goal ${goalId}`)
        try {
          await axios.delete(`/v1/savings/goals/${goalId}`)
          setRefreshCounter(counter => counter + 1)
          setEditModalState(null)
        } catch (e) {
          if (e instanceof AxiosError && e.response?.status === 400) {
            toast.error("Goal removal failed — goal must be empty")
          } else {
            toast.error("Goal removal failed — unknown error")
          }
          console.error(e)
        }
      }
    })
  }

  const reorderGoals = async (indexA: number, indexB: number) => {
    console.log(`Swapping goal ${indexA} with ${indexB}`)

    try {
      await axios.post(`/v1/savings/goals/reorder`, {
        goalA: sortedGoals[indexA][0],
        goalB: sortedGoals[indexB][0]
      })
      setRefreshCounter(counter => counter + 1)
    } catch (e) {
      toast.error("Goal moving failed — unknown error")
      console.error(e)
    }
  }

  const reorderAssets = async (indexA: number, indexB: number) => {
    console.log(`Swapping asset ${indexA} with ${indexB}`, sortedAssets[indexA][0], sortedAssets[indexB][0])
    try {
      await axios.post(`/v1/savings/assets/reorder`, {
        assetA: sortedAssets[indexA][0],
        assetB: sortedAssets[indexB][0]
      })
      setRefreshCounter(counter => counter + 1)
    } catch (e) {
      toast.error("Asset moving failed — unknown error")
      console.error(e)
    }
  }

  useEffect(() => {
    if (clickState.source !== null && _.isEqual(clickState.source, clickState.target)) {
      setAdjustmentTarget({ assetId: clickState.source.assetId, goalId: clickState.source.goalId })
    } else {
      setAdjustmentTarget(null)
    }
  }, [clickState])

  useEffect(() => {
    const fn = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onEscape()
      }
    }
    document.addEventListener("keydown", fn)
    return () => {
      document.removeEventListener("keydown", fn)
    }
  }, [onEscape])

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true)
        const response = await axios.get("/v1/savings/summary")
        setSummary(response.data)
      } catch (e) {
        toast.error("Failed to fetch savings summary — unknown error")
        console.error(e)
      } finally {
        setLoading(false)
      }
    }

    fetch()
  }, [refreshCounter, axios])

  return <div className="py-3">
    {loading ? <div className="text-center my-3"><Spinner animation="border" /></div> :
      <>
        <div className="d-flex justify-content-between align-items-center">
          <h3>Savings</h3>
          <ToggleButton type="checkbox" id="edition-toggle" value="1" size="sm" variant="outline-secondary" checked={edition} onChange={(e) => setEdition(e.currentTarget.checked)}><FontAwesomeIcon icon={faEdit} /> Edit</ToggleButton>
        </div>
        <EditionModal {...editModalState!} onHide={() => setEditModalState(null)} />
        <TransferModal source={clickState.source} target={clickState.target} onHide={cancelCellClicked} summary={summary} onTransfer={transfer} />
        <AdjustmentModal show={!!adjustmentTarget} onHide={() => setAdjustmentTarget(null)} onAdjust={adjust} />
        <div className={styles.tableContainer}>
          <Table striped hover>
            <thead>
              <tr>
                <th>Asset</th>
                {summary.goals && sortedGoals.map(([goalId, goal], index) => {
                  return <th key={goalId}>
                    {goal.name}
                    {edition && <span className={classNames(styles.actions, "d-flex", "gap-1")}>
                      <Button size="sm" variant="outline-secondary" disabled={index === 0} onClick={() => reorderGoals(index, index - 1)}><FontAwesomeIcon icon={faArrowLeft} /></Button>
                      <Button size="sm" variant="outline-primary" onClick={() => openGoalRenameModal(goalId)}><FontAwesomeIcon icon={faEdit} /></Button>
                      <Button size="sm" variant="outline-danger" onClick={() => openGoalRemovalModal(goalId)}><FontAwesomeIcon icon={faTrash} /></Button>
                      <Button size="sm" variant="outline-secondary" disabled={index === Object.keys(summary.goals).length - 1} onClick={() => reorderGoals(index, index + 1)}><FontAwesomeIcon icon={faArrowRight} /></Button>
                    </span>}
                  </th>
                })}
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {summary.assets && sortedAssets.map(([assetId, asset], index) => {
                const unwrappedAsset = "managedSavingsAccount" in asset ? asset.managedSavingsAccount : asset.custom

                let unaccounted = unwrappedAsset.balance.amount

                return <tr key={assetId}>
                  <td className={styles.asset}>
                    <span>{unwrappedAsset.name}</span>
                    {edition && <span className={classNames(styles.actions, "d-flex", "gap-1")}>
                      <Button size="sm" variant="outline-secondary" disabled={index === 0} onClick={() => reorderAssets(index, index - 1)}><FontAwesomeIcon icon={faArrowUp} /></Button>
                      <Button size="sm" variant="outline-primary" onClick={() => openAssetRenameModal(assetId)}><FontAwesomeIcon icon={faEdit} /></Button>
                      <Button size="sm" variant="outline-danger" onClick={() => openAssetRemovalModal(assetId)}><FontAwesomeIcon icon={faTrash} /></Button>
                      <Button size="sm" variant="outline-secondary" disabled={index === Object.keys(summary.assets).length - 1} onClick={() => reorderAssets(index, index + 1)}><FontAwesomeIcon icon={faArrowDown} /></Button>
                    </span>}
                    <span className={styles.kind}>{unwrappedAsset.kind}</span>
                  </td>
                  {summary.goals && sortedGoals.map(([goalId, goal]) => {
                    const mappingEntry = summary.mapping[goalId]?.[assetId]
                    if (mappingEntry) {
                      unaccounted -= mappingEntry.amount
                    }
                    return (
                      <td
                        key={goalId}
                        onClick={() => onCellClicked(assetId, goalId)}
                        className={classNames({
                          [styles.source]: (clickState.source?.assetId === assetId) && (clickState.source?.goalId === goalId),
                          [styles.target]: (clickState.target?.assetId === assetId) && (clickState.target?.goalId === goalId)
                        })}
                      >
                        {mappingEntry && <AmountComponent amount={mappingEntry.amount} currency={mappingEntry.currency} />}
                      </td>
                    )
                  }
                  )}
                  <td onDoubleClick={() => setAdjustmentTarget({ assetId })}>
                    <AmountComponent amount={unwrappedAsset.balance.amount} currency={unwrappedAsset.balance.currency} /><br/>
                    {Math.abs(unaccounted) > 1 && <span className="small">(<AmountComponent amount={unaccounted} currency={unwrappedAsset.balance.currency} coloured={true} /> {unaccounted > 0 ? "remaining" : "missing"})</span>}
                  </td>
                </tr>
              })
              }
            </tbody>
            <tfoot>
              <tr>
                <th>Total</th>
                {summary.goals && sortedGoals.map(([goalId,]) => {
                  return <th key={goalId}>
                    {goalTotals[goalId] &&
                      <AmountComponent amount={goalTotals[goalId].amount} currency={goalTotals[goalId].currency} />
                    }
                  </th>
                })}
                <th></th>
                <th></th>
              </tr>
            </tfoot>
          </Table>
        </div>
        {edition && <div className="d-flex gap-1 justify-content-center">
          <Button variant="success" size="sm" onClick={openAssetAdditionModal}><FontAwesomeIcon icon={faPlus} /> Add asset</Button>
          <Button variant="success" size="sm" onClick={openGoalAdditionModal}><FontAwesomeIcon icon={faPlus} /> Add goal</Button>
        </div>}
      </>
    }
  </div>

}
